import { useState, useCallback, useEffect } from 'react';
import type { TopCategoryViewModel } from 'api/types/top-category';
import type { ProductTypeViewModel } from 'api/types/product-type';
import type { SpecConstraintViewModel } from 'api/types/product-type/SpecConstraintViewModel';
import type { ProductTypeSpecRule } from 'api/types/product-type/ProductTypeSpecRule';
import type { ConditionViewModel } from 'api/types/condition';
import type { CityViewModel } from 'api/types/city';
import type { DistrictViewModel } from 'api/types/district';
import type { ListingQueryModel } from 'api/types/listings/ListingQueryModel';
import type { SpecFilter } from 'api/types/listings/SpecFilter';
import type { BoundaryFilter } from 'api/types/common/BoundaryFilter';
import { topCategoryService } from 'api/services/TopCategoryService';
import { productTypeService } from 'api/services/ProductTypeService';
import { listingService } from 'api/services/ListingService';
import { conditionService } from 'api/services/ConditionService';
import { cityService } from 'api/services/CityService';
import { districtService } from 'api/services/DistrictService';
import { sortOptionToOrderBy } from 'api/utils/listingMapper';
import type { ListingSortOption } from 'api/utils/listingMapper';
import type { AdListing } from 'types/adListing';
import { listingViewModelToAdListing } from 'api/utils/listingMapper';
import type { ListingFilterState, SpecOperator } from 'types/listingFilterState';
import { defaultListingFilterState } from 'types/listingFilterState';
import { SpecValueType } from 'api/types/product-type/SpecConstraintViewModel';
import { SpecSelectionMode } from 'api/types/product-type/SpecConstraintViewModel';
import { istanbulCalendarDayStartUtcIso } from 'utils/istanbulListingDate';
import {
  normalizeSpecConstraintList,
  normalizeSpecValueType,
} from 'utils/specConstraintUtils';

const DEFAULT_PAGE_SIZE = 12;

function buildCreatedAtBoundary(applied: ListingFilterState): BoundaryFilter<string> | undefined {
  const now = Date.now();
  const iso = (ts: number) => new Date(ts).toISOString();
  if (applied.timePreset === '24h') {
    return { lowerBound: iso(now - 24 * 60 * 60 * 1000), upperBound: iso(now) };
  }
  if (applied.timePreset === '7d') {
    return { lowerBound: iso(now - 7 * 24 * 60 * 60 * 1000), upperBound: iso(now) };
  }
  if (applied.timePreset === '30d') {
    return { lowerBound: iso(now - 30 * 24 * 60 * 60 * 1000), upperBound: iso(now) };
  }
  // Single date "from this date onward" (feature 013, US9)
  if (applied.createdAtFrom) {
    return { lowerBound: istanbulCalendarDayStartUtcIso(applied.createdAtFrom) };
  }
  return undefined;
}

/**
 * Build ListingQueryModel from applied filter state, sort, and pagination.
 * Omits empty values; price uses LowerBound / UpperBound (ExactMatch omitted from UI but still forwarded if present in state).
 */
function appliedToQuery(
  applied: ListingFilterState,
  sortOption: ListingSortOption,
  page: number,
  pageSize: number,
  constraints: SpecConstraintViewModel[]
): ListingQueryModel {
  const q: ListingQueryModel = {
    Pagination: { pageNumber: page, pageSize },
    OrderBy: sortOptionToOrderBy(sortOption),
  };
  if (applied.topCategoryId != null) q.TopCategoryId = applied.topCategoryId;
  if (applied.secondaryCategoryId != null) q.SecondaryCategoryId = applied.secondaryCategoryId;
  if (applied.productTypeId != null) q.ProductTypeId = applied.productTypeId;
  if (applied.conditionId != null) q.ConditionId = applied.conditionId;
  if (applied.cityId != null) q.CityId = applied.cityId;
  if (applied.districtId != null) q.DistrictId = applied.districtId;
  if (applied.isNew !== null && applied.isNew !== undefined) q.IsNew = applied.isNew;
  if (applied.sellerAccountType != null) q.SellerAccountType = applied.sellerAccountType;

  const price: BoundaryFilter<number> = {};
  if (applied.price.lowerBound != null) price.lowerBound = Number(applied.price.lowerBound);
  if (applied.price.upperBound != null) price.upperBound = Number(applied.price.upperBound);
  if (applied.price.exactMatch != null) price.exactMatch = Number(applied.price.exactMatch);
  if (Object.keys(price).length > 0) q.Price = price;

  const specs: SpecFilter[] = [];
  constraints.forEach((c) => {
    const valueType = normalizeSpecValueType(c.valueType);
    if (valueType === SpecValueType.Enum) {
      const multiValues = applied.specMultiValues[c.key] ?? [];
      if (multiValues.length > 0) {
        multiValues.forEach((v) => {
          const trimmed = String(v).trim();
          if (trimmed === '') return;
          specs.push({ key: c.key, value: trimmed, valueType, operator: '=' });
        });
        return;
      }
    }

    const raw = applied.specValues[c.key];
    if (raw == null) return;
    const trimmed = String(raw).trim();
    if (trimmed === '') return;

    const operator =
      valueType === SpecValueType.Number ? applied.specOperators[c.key] ?? '=' : '=';

    specs.push({
      key: c.key,
      value: trimmed,
      valueType,
      operator,
    });
  });
  if (specs.length > 0) q.Specs = specs;

  const createdAt = buildCreatedAtBoundary(applied);
  if (createdAt) q.CreatedAt = createdAt;

  return q;
}

export interface UseListingSearchResult {
  listings: AdListing[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  fetchError: string | null;
  topCategories: TopCategoryViewModel[];
  secondaryCategories: { id: number; name?: string | null }[];
  productTypes: ProductTypeViewModel[];
  constraints: SpecConstraintViewModel[];
  constraintsLoading: boolean;
  constraintsError: string | null;
  rules: ProductTypeSpecRule[];
  conditions: ConditionViewModel[];
  cities: CityViewModel[];
  districts: DistrictViewModel[];
  draft: ListingFilterState;
  applied: ListingFilterState;
  setDraft: React.Dispatch<React.SetStateAction<ListingFilterState>>;
  setDraftCategory: (level: 'top' | 'secondary' | 'productType', id: number | null) => void;
  setDraftSpecValue: (key: string, value: string) => void;
  setDraftSpecMultiValue: (key: string, values: string[]) => void;
  setDraftSpecOperator: (key: string, operator: import('types/listingFilterState').SpecOperator) => void;
  setDraftPrice: (field: 'lowerBound' | 'upperBound', value: number | null) => void;
  applyFilters: () => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  sortOption: ListingSortOption;
  setSortOption: (option: ListingSortOption) => void;
  refetch: () => void;
}

export function useListingSearch(initialPageSize: number = DEFAULT_PAGE_SIZE): UseListingSearchResult {
  const [topCategories, setTopCategories] = useState<TopCategoryViewModel[]>([]);
  const [conditions, setConditions] = useState<ConditionViewModel[]>([]);
  const [cities, setCities] = useState<CityViewModel[]>([]);
  const [districts, setDistricts] = useState<DistrictViewModel[]>([]);
  const [productTypes, setProductTypes] = useState<ProductTypeViewModel[]>([]);
  const [constraints, setConstraints] = useState<SpecConstraintViewModel[]>([]);
  const [constraintsLoading, setConstraintsLoading] = useState(false);
  const [constraintsError, setConstraintsError] = useState<string | null>(null);
  const [appliedConstraints, setAppliedConstraints] = useState<SpecConstraintViewModel[]>([]);
  const [rules, setRules] = useState<ProductTypeSpecRule[]>([]);

  const [draft, setDraft] = useState<ListingFilterState>(defaultListingFilterState);
  const [applied, setApplied] = useState<ListingFilterState>(defaultListingFilterState);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(initialPageSize);
  const [sortOption, setSortOptionState] = useState<ListingSortOption>('newest');

  const [listings, setListings] = useState<AdListing[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const secondaryCategories = (() => {
    if (draft.topCategoryId == null) return [];
    const top = topCategories.find((t) => t.id === draft.topCategoryId);
    return (top?.secondaryCategories ?? []).map((s) => ({ id: s.id, name: s.name }));
  })();

  const setDraftCategory = useCallback(
    (level: 'top' | 'secondary' | 'productType', id: number | null) => {
      // Clear spec filter state when taxonomy changes (draft + applied) per FR-009
      setApplied((prev) => ({
        ...prev,
        specValues: {},
        specMultiValues: {},
        specOperators: {},
      }));
      setAppliedConstraints([]);

      setDraft((prev) => {
        const next = { ...prev };
        // Clear category-specific state when category changes
        next.specValues = {};
        next.specMultiValues = {};
        next.specOperators = {};
        
        if (level === 'top') {
          next.topCategoryId = id;
          next.secondaryCategoryId = null;
          next.productTypeId = null;
          setProductTypes([]);
          if (id != null) {
            const top = topCategories.find((t) => t.id === id);
            const secondaries = top?.secondaryCategories ?? [];
            if (secondaries.length === 1) {
              next.secondaryCategoryId = secondaries[0].id;
              const types = secondaries[0].productTypes ?? [];
              setProductTypes(types);
              if (types.length === 1) {
                next.productTypeId = types[0].id;
                productTypeService.getSpecConstraints(types[0].id).then((r) => {
                  if (r.isSuccess && r.data) setConstraints(r.data);
                  else setConstraints([]);
                });
              }
            }
          }
        } else if (level === 'secondary') {
          next.secondaryCategoryId = id;
          next.productTypeId = null;
          if (id != null) {
            const top = topCategories.find((t) => t.id === next.topCategoryId);
            const secondary = top?.secondaryCategories?.find((s) => s.id === id);
            const types = secondary?.productTypes ?? [];
            setProductTypes(types);
            if (types.length === 1) {
              next.productTypeId = types[0].id;
            }
          } else setProductTypes([]);
        } else {
          next.productTypeId = id;
        }
        return next;
      });
    },
    [topCategories]
  );

  const setDraftSpecValue = useCallback((key: string, value: string) => {
    setDraft((prev) => ({
      ...prev,
      specValues: { ...prev.specValues, [key]: value },
    }));
  }, []);

  const setDraftSpecMultiValue = useCallback((key: string, values: string[]) => {
    setDraft((prev) => ({
      ...prev,
      specMultiValues: { ...prev.specMultiValues, [key]: values },
    }));
  }, []);

  const setDraftSpecOperator = useCallback((key: string, operator: SpecOperator) => {
    setDraft((prev) => ({
      ...prev,
      specOperators: { ...prev.specOperators, [key]: operator },
    }));
  }, []);

  const setDraftPrice = useCallback(
    (field: 'lowerBound' | 'upperBound', value: number | null) => {
      setDraft((prev) => ({
        ...prev,
        price: { ...prev.price, [field]: value },
      }));
    },
    []
  );

  const fetchListings = useCallback(() => {
    setLoading(true);
    setFetchError(null);
    const query = appliedToQuery(applied, sortOption, currentPage, pageSize, appliedConstraints);
    listingService
      .getAllActive(query)
      .then((result) => {
        if (result.isSuccess && result.data) {
          setListings(result.data.map(listingViewModelToAdListing));
          setTotalPages(result.pagination?.totalPageCount ?? 1);
        } else {
          setListings([]);
          setTotalPages(1);
          setFetchError(result.message ?? 'Liste alınamadı.');
        }
      })
      .catch((err) => {
        setListings([]);
        setTotalPages(1);
        setFetchError(err instanceof Error ? err.message : 'Bir hata oluştu.');
      })
      .finally(() => setLoading(false));
  }, [applied, sortOption, currentPage, pageSize, appliedConstraints]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const applyFilters = useCallback(() => {
    setApplied(draft);
    setAppliedConstraints(constraints);
    setCurrentPage(1);
  }, [draft, constraints]);

  const clearFilters = useCallback(() => {
    setDraft(defaultListingFilterState);
    setApplied(defaultListingFilterState);
    setCurrentPage(1);
    setProductTypes([]);
    setConstraints([]);
    setConstraintsError(null);
    setConstraintsLoading(false);
    setAppliedConstraints([]);
    setRules([]);
  }, []);

  const setPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const setSortOption = useCallback((option: ListingSortOption) => {
    setSortOptionState(option);
  }, []);

  useEffect(() => {
    topCategoryService.getPopulated().then((r) => {
      if (r.isSuccess && r.data) setTopCategories(r.data);
    });
    conditionService.getAll().then((r) => {
      if (r.isSuccess && r.data) setConditions(r.data);
    });
    cityService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }).then((r) => {
      if (r.isSuccess && r.data) setCities(r.data);
    });
  }, []);

  useEffect(() => {
    if (draft.cityId == null) {
      setDistricts([]);
      return;
    }
    districtService.getAll({ cityId: draft.cityId }).then((r) => {
      if (r.isSuccess && r.data) setDistricts(r.data);
      else setDistricts([]);
    });
  }, [draft.cityId]);

  useEffect(() => {
    const productTypeId = draft.productTypeId;
    if (productTypeId == null) {
      setConstraints([]);
      setConstraintsLoading(false);
      setConstraintsError(null);
      setRules([]);
      return;
    }

    let cancelled = false;
    setConstraintsLoading(true);
    setConstraintsError(null);

    productTypeService
      .getSpecConstraints(productTypeId)
      .then((r) => {
        if (cancelled) return;
        setConstraintsLoading(false);
        if (r.isSuccess) {
          const list = normalizeSpecConstraintList(r.data);
          setConstraints(list);
          setConstraintsError(null);
        } else {
          setConstraints([]);
          setConstraintsError(r.message ?? 'Özellikler yüklenemedi.');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setConstraintsLoading(false);
        setConstraints([]);
        setConstraintsError(
          err instanceof Error ? err.message : 'Özellikler yüklenemedi.'
        );
      });

    productTypeService
      .getRules(productTypeId)
      .then((r) => {
        if (cancelled) return;
        setRules(r.isSuccess && r.data ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setRules([]);
      });

    return () => {
      cancelled = true;
    };
  }, [draft.productTypeId]);

  const refetch = useCallback(() => {
    fetchListings();
  }, [fetchListings]);

  return {
    listings,
    totalPages,
    currentPage,
    pageSize,
    loading,
    fetchError,
    topCategories,
    secondaryCategories,
    productTypes,
    constraints,
    constraintsLoading,
    constraintsError,
    rules,
    conditions,
    cities,
    districts,
    draft,
    applied,
    setDraft,
    setDraftCategory,
    setDraftSpecValue,
    setDraftSpecMultiValue,
    setDraftSpecOperator,
    setDraftPrice,
    applyFilters,
    clearFilters,
    setPage,
    sortOption,
    setSortOption,
    refetch,
  };
}

export { SpecValueType };
